import {
	useApiGetEntityCounts,
	useApiHasPreviewAccess,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useIntegration,
	useSuspenseFeatureAccess,
	useUpdateSecodaEntity,
} from '../../api';
import { useSuspenseSecodaEntity } from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import { getDefaultTab } from '../../components/EntityPageLayout/utils';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import type { OverviewPageTab } from '../../components/Overview/OverviewPageTabs';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import UrlPreview from '../../components/Preview/UrlPreview';
import { QueryList } from '../../components/QueryListV2/QueryList';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import MonitorsTab from '../TableEntityPage/TableEntityTabs/MonitorsTab';
import type { DjangoValueType } from '../TemplatePage/types';

const InnerChartPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const navigateToTab = useTabNavigate();

	const {
		lineageAccess,
		monitoringAccess,
		resourcePreviewAccess,
		secodaCoreAccess,
	} = useSuspenseFeatureAccess();

	const { data: chart } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('chart_entity/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSecodaEntity({});

	const integrationId = chart?.integration || '';

	const { data: integration } = useIntegration({
		id: integrationId,
		options: { enabled: !!integrationId },
	});

	const { data: previewPermissionGranted } = useApiHasPreviewAccess(
		{
			pathParams: {
				integrationId: integrationId,
			},
		},
		{ enabled: !!integrationId }
	);

	const { data: entityCounts, isLoading } = useApiGetEntityCounts(
		{
			queryParams: {
				entity_id: id || '',
			},
		},
		{
			enabled: !!id,
		}
	);

	const { hasPermission: hasQueriesAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Queries.Read',
	});

	const { hasPermission: hasQuestionsAccess } = useCheckIAMPermission({
		v1AllowedRoles: [
			UserRole.ADMIN,
			UserRole.EDITOR,
			UserRole.VIEWER,
			UserRole.GUEST,
		],
		v2Permission: 'Questions.Read',
	});

	const updateChart = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely && chart) {
				await mutateAsync({
					data: {
						id: chart.id,
						[key]: value,
					},
				});
				trackEvent('chart/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, chart, user, workspace]
	);

	if (!chart) {
		return <EntityPageLayoutSkeleton />;
	}

	const showPreviewTab =
		integration?.support_chart_preview &&
		!!previewPermissionGranted &&
		resourcePreviewAccess;

	const documentationFirst = (chart.definition?.length || 0) > 2;

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('chart/lineage/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			chart.id,
			chart.published,
			EntityType.chart,
			chart.native_type
		);

	const tabs = useMemo(() => {
		const tabArray: OverviewPageTab[] = [
			...(showPreviewTab
				? [
						{
							value: 'preview',
							label: 'Preview',
							component: (
								<UrlPreview
									integration={integration?.type as string}
									entity={chart}
								/>
							),
						},
					]
				: []),
			{
				value: 'lineage',
				label: 'Lineage',
				condition: !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'monitors',
				label: 'Monitors',
				condition: !!monitoringAccess,
				component: (
					<MonitorsTab
						integrationId={chart.integration}
						upstreamOf={chart.id}
						canCreateMonitors={true}
					/>
				),
			},
			{
				value: 'queries',
				label: 'Queries',
				condition:
					!isLoading && Boolean(entityCounts?.query_count) && hasQueriesAccess,
				component: <QueryList entity={chart} />,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: hasQuestionsAccess && !!secodaCoreAccess,
				component: <EntityDiscussions entity={chart} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={chart} />,
			},
		];

		if (documentationFirst) {
			const docTabIndex = tabArray.findIndex(
				(t) => t.value === 'documentation'
			);
			if (docTabIndex !== -1) {
				const [docTab] = tabArray.splice(docTabIndex, 1);
				tabArray.unshift(docTab);
			}
		}

		return tabArray;
	}, [
		showPreviewTab,
		integration?.type,
		chart,
		isLoading,
		entityCounts?.query_count,
		documentationFirst,
		hasQueriesAccess,
		hasQuestionsAccess,
		lineageAccess,
		monitoringAccess,
		secodaCoreAccess,
	]);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(tabs), [tabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={chart.id}
			name="Chart"
			isReadOnlyTitle
			entity={chart}
			icon={<EntityLogo size={32} entity={chart} />}
			updateEntity={updateChart}
			withDescription
			{...getEntityPageSidebarProps(EntityType.chart)}
		>
			<OverviewPageTabs
				key={chart.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={tabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// by memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function ChartPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerChartPage id={id} />;
}
